import React, { useCallback, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import Cropper, { Area } from 'react-easy-crop'
import 'react-easy-crop/react-easy-crop.css'
import {
  encodeProfileImageCrop,
  isAcceptedProfileImageMimeType,
  isProfileImageDimensionValid,
  isProfileImageFileSizeValid,
  ProfileImageCropArea
} from '../../../pure-js/libs/ProfileImage'
import GrooverText from './GrooverText'
import { getGrooverText } from '../libs/GrooverTextRepository'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
`

const Dialog = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
`

const DialogHeader = styled.div`
  padding: 20px 24px 0;
`

const DialogTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
`

const CropContainer = styled.div`
  position: relative;
  width: 100%;
  height: 320px;
  background: #111827;
  margin-top: 16px;
`

const ZoomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px 0;
`

const ZoomSlider = styled.input`
  flex: 1;
  accent-color: #7c3aed;
  cursor: pointer;
`

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px;
`

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition:
    background 0.2s,
    opacity 0.2s;

  ${({ variant }) =>
    variant === 'primary'
      ? `
    background: #7c3aed;
    color: #ffffff;
    &:hover:not(:disabled) { background: #6d28d9; }
  `
      : `
    background: #f3f4f6;
    color: #374151;
    &:hover:not(:disabled) { background: #e5e7eb; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const loadImageElement = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', () => reject(new Error('Failed to decode image')))
    image.src = url
  })

const toProfileImageCropArea = (area: Area): ProfileImageCropArea => ({
  x: area.x,
  y: area.y,
  width: area.width,
  height: area.height
})

type ProfileImageEditorProps = {
  file: File | null
  onClose: () => void
  onComplete: (blob: Blob) => void
  onValidationError: (textKey: string) => void
}

export const ProfileImageEditor: React.FC<ProfileImageEditorProps> = ({
  file,
  onClose,
  onComplete,
  onValidationError
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [encoding, setEncoding] = useState(false)

  useEffect(() => {
    if (!file) {
      setImageUrl(null)
      return
    }

    let revokedUrl: string | null = null
    let cancelled = false

    const validate = async () => {
      if (!isAcceptedProfileImageMimeType(file.type)) {
        onValidationError('profileImageErrorUnsupportedType')
        onClose()
        return
      }
      if (!isProfileImageFileSizeValid(file.size)) {
        onValidationError('profileImageErrorTooLarge')
        onClose()
        return
      }

      const url = URL.createObjectURL(file)
      revokedUrl = url

      try {
        const image = await loadImageElement(url)
        if (cancelled) {
          return
        }
        if (!isProfileImageDimensionValid(image.naturalWidth, image.naturalHeight)) {
          onValidationError('profileImageErrorTooSmall')
          onClose()
          return
        }
        setImageUrl(url)
        revokedUrl = null
      } catch {
        if (!cancelled) {
          onValidationError('profileImageErrorDecodeFailed')
          onClose()
        }
      }
    }

    void validate()

    return () => {
      cancelled = true
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl)
      }
    }
  }, [file, onClose, onValidationError])

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl)
      }
    }
  }, [imageUrl])

  const onCropComplete = useCallback((_croppedArea: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels)
  }, [])

  const handleConfirm = async () => {
    if (!imageUrl || !croppedAreaPixels || encoding) {
      return
    }

    setEncoding(true)
    try {
      const image = await loadImageElement(imageUrl)
      const blob = await encodeProfileImageCrop(image, toProfileImageCropArea(croppedAreaPixels))
      onComplete(blob)
      onClose()
    } catch {
      onValidationError('profileImageErrorDecodeFailed')
    } finally {
      setEncoding(false)
    }
  }

  if (!file || !imageUrl) {
    return null
  }

  return (
    <Overlay role="presentation" onClick={onClose}>
      <Dialog role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>
            <GrooverText textKey="profileImageCropTitle" />
          </DialogTitle>
        </DialogHeader>
        <CropContainer>
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </CropContainer>
        <ZoomRow>
          <ZoomSlider
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            aria-label={getGrooverText('profileImageCropZoom')}
            onChange={(event) => setZoom(Number(event.target.value))}
            disabled={encoding}
          />
        </ZoomRow>
        <DialogActions>
          <ActionButton type="button" onClick={onClose} disabled={encoding}>
            {getGrooverText('profileImageCropCancel')}
          </ActionButton>
          <ActionButton type="button" variant="primary" onClick={() => void handleConfirm()} disabled={encoding}>
            {getGrooverText('profileImageCropConfirm')}
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Overlay>
  )
}
